
import React, { useEffect, useRef, ReactNode } from 'react'
import { cn } from "@/lib/utils"
import { useIsMobile } from '@/hooks/use-mobile'
import { GridRow } from './grid-row'

interface MiniProfileGridProps {
  profileContent?: ReactNode
  linkItems?: ReactNode[]
  bulletinItems?: ReactNode[]
  gradientColor?: string
  className?: string
}

interface ColorBlock {
  type: 'color'
  style: string
}

type Pattern = ColorBlock

const BLOCK_COLOR = '#222222'
const BULLETIN_GRADIENT = 'linear-gradient(to right, #243949 0%, #517fa4 100%)'

export function MiniProfileGrid({
  profileContent,
  linkItems = [],
  bulletinItems = [],
  gradientColor = '#221F26',
  className
}: MiniProfileGridProps) {
  const gridRef = useRef<HTMLDivElement>(null)
  const rowRefs = useRef<(HTMLDivElement | null)[]>([])
  const isMobile = useIsMobile()
  
  const mouseXRef = useRef(window.innerWidth / 2)
  const touchStartXRef = useRef(0)
  const rowPositionsRef = useRef<number[]>([])
  const animationFrameRef = useRef<number | null>(null)

  const rows = isMobile ? 8 : 7  // Keeping at 8 rows for mobile
  const columns = isMobile ? 4 : 7 // Kept at 4 columns for mobile

  const totalItems = rows * columns

  const finalItems = Array(totalItems).fill(null)

  const getColorBlock = (index: number, isForBulletin: boolean = false): Pattern => {
    if (isForBulletin) {
      return {
        type: 'color',
        style: BULLETIN_GRADIENT
      }
    }
    return {
      type: 'color',
      style: BLOCK_COLOR
    }
  }

  const getBulletinPositionWeights = () => {
    if (isMobile) {
      return [
        { row: 0, col: 0, weight: 5 },
        { row: 0, col: 1, weight: 6 },
        { row: 0, col: 2, weight: 6 },
        { row: 0, col: 3, weight: 5 },

        { row: 1, col: 0, weight: 7 },
        { row: 1, col: 1, weight: 9 },
        { row: 1, col: 2, weight: 9 },
        { row: 1, col: 3, weight: 7 },

        { row: 2, col: 0, weight: 4 },
        { row: 2, col: 1, weight: 4 },
        { row: 2, col: 2, weight: 4 },
        { row: 2, col: 3, weight: 4 },

        { row: 3, col: 0, weight: 3 },
        { row: 3, col: 1, weight: 0 },
        { row: 3, col: 2, weight: 6 },
        { row: 3, col: 3, weight: 5 },

        { row: 4, col: 0, weight: 4 },
        { row: 4, col: 1, weight: 4 },
        { row: 4, col: 2, weight: 4 },
        { row: 4, col: 3, weight: 4 },

        { row: 5, col: 0, weight: 6 },
        { row: 5, col: 1, weight: 8 },
        { row: 5, col: 2, weight: 8 },
        { row: 5, col: 3, weight: 6 },

        { row: 6, col: 0, weight: 8 },
        { row: 6, col: 1, weight: 10 },
        { row: 6, col: 2, weight: 10 },
        { row: 6, col: 3, weight: 8 },

        { row: 7, col: 0, weight: 5 },
        { row: 7, col: 1, weight: 6 },
        { row: 7, col: 2, weight: 6 },
        { row: 7, col: 3, weight: 5 },
      ];
    } else {
      return [
        { row: 0, col: 0, weight: 6 },
        { row: 0, col: 1, weight: 7 },
        { row: 0, col: 2, weight: 7 },
        { row: 0, col: 3, weight: 8 },
        { row: 0, col: 4, weight: 7 },
        { row: 0, col: 5, weight: 7 },
        { row: 0, col: 6, weight: 6 },

        { row: 1, col: 0, weight: 5 },
        { row: 1, col: 1, weight: 5 },
        { row: 1, col: 2, weight: 5 },
        { row: 1, col: 3, weight: 0 },
        { row: 1, col: 4, weight: 5 },
        { row: 1, col: 5, weight: 5 },
        { row: 1, col: 6, weight: 5 },

        { row: 2, col: 0, weight: 4 },
        { row: 2, col: 1, weight: 4 },
        { row: 2, col: 2, weight: 4 },
        { row: 2, col: 3, weight: 4 },
        { row: 2, col: 4, weight: 4 },
        { row: 2, col: 5, weight: 4 },
        { row: 2, col: 6, weight: 4 },

        { row: 3, col: 0, weight: 8 },
        { row: 3, col: 1, weight: 9 },
        { row: 3, col: 2, weight: 10 },
        { row: 3, col: 3, weight: 10 },
        { row: 3, col: 4, weight: 10 },
        { row: 3, col: 5, weight: 9 },
        { row: 3, col: 6, weight: 8 },

        { row: 4, col: 0, weight: 7 },
        { row: 4, col: 1, weight: 8 },
        { row: 4, col: 2, weight: 9 },
        { row: 4, col: 3, weight: 10 },
        { row: 4, col: 4, weight: 9 },
        { row: 4, col: 5, weight: 8 },
        { row: 4, col: 6, weight: 7 },

        { row: 5, col: 0, weight: 6 },
        { row: 5, col: 1, weight: 7 },
        { row: 5, col: 2, weight: 8 },
        { row: 5, col: 3, weight: 9 },
        { row: 5, col: 4, weight: 8 },
        { row: 5, col: 5, weight: 7 },
        { row: 5, col: 6, weight: 6 },

        { row: 6, col: 0, weight: 5 },
        { row: 6, col: 1, weight: 5 },
        { row: 6, col: 2, weight: 6 },
        { row: 6, col: 3, weight: 7 },
        { row: 6, col: 4, weight: 6 },
        { row: 6, col: 5, weight: 5 },
        { row: 6, col: 6, weight: 5 },
      ];
    }
  };

  const distributeBulletins = () => {
    const positions = getBulletinPositionWeights();
    const sortedPositions = [...positions].sort((a, b) => b.weight - a.weight);
    const decorativeBlocks = [
      { row: 0, col: 0, style: 'linear-gradient(90deg, hsla(277, 75%, 84%, 1) 0%, hsla(297, 50%, 51%, 1) 100%)' },
      { row: isMobile ? 7 : 6, col: 0, style: 'linear-gradient(90deg, hsla(29, 92%, 70%, 1) 0%, hsla(0, 87%, 73%, 1) 100%)' },
      { row: 5, col: 0, style: 'linear-gradient(90deg, hsla(221, 45%, 73%, 1) 0%, hsla(220, 78%, 29%, 1) 100%)' },
    ];
    
    decorativeBlocks.forEach(block => {
      const index = block.row * columns + block.col;
      if (index < totalItems && finalItems[index] === null) {
        finalItems[index] = {
          type: 'color',
          style: block.style
        };
      }
    });
    
    let bulletinIndex = 0;
    const availablePositions = sortedPositions.filter(pos => {
      const index = pos.row * columns + pos.col;
      return pos.weight > 0 && index < totalItems && finalItems[index] === null;
    });
    
    for (const position of availablePositions) {
      if (bulletinIndex >= bulletinItems.length) break;
      
      const index = position.row * columns + position.col;
      finalItems[index] = bulletinItems[bulletinIndex++];
    }
    
    if (bulletinIndex < bulletinItems.length) {
      for (let i = 0; i < totalItems; i++) {
        if (finalItems[i] === null && bulletinIndex < bulletinItems.length) {
          finalItems[i] = bulletinItems[bulletinIndex++];
        }
      }
    }
    
    for (let i = 0; i < totalItems; i++) {
      if (finalItems[i] === null) {
        const useBulletinStyle = Math.random() < 0.7;
        finalItems[i] = getColorBlock(i, useBulletinStyle);
      }
    }
  };

  // Initialize profile content placement
  if (profileContent) {
    if (isMobile) {
      const profileIndex = (3 * columns) + 1
      finalItems[profileIndex] = profileContent
      
      finalItems[profileIndex + 1] = null
    } else {
      const profileIndex = (1 * columns) + 3
      finalItems[profileIndex] = profileContent
    }
  }

  // Handle link items placement
  let linkIndex = 0;
  
  if (isMobile) {
    const linkRowIndices = [2, 4];
    
    linkRowIndices.forEach(rowIdx => {
      for (let col = 0; col < columns && linkIndex < linkItems.length; col++) {
        const position = (rowIdx * columns) + col;
        if (finalItems[position] === null) {
          finalItems[position] = linkItems[linkIndex++];
        }
      }
    });
  } else {
    const row2Positions = [0, 1, 2, 4, 5, 6]
    row2Positions.forEach(col => {
      if (linkIndex < linkItems.length) {
        finalItems[(1 * columns) + col] = linkItems[linkIndex++]
      }
    })
    
    for (let col = 0; col < columns && linkIndex < linkItems.length; col++) {
      finalItems[(2 * columns) + col] = linkItems[linkIndex++]
    }
  }

  // IMPORTANT: Call the distributeBulletins function to properly distribute bulletin content
  distributeBulletins();

  useEffect(() => {
    rowPositionsRef.current = Array(rows).fill(0);
  }, [rows]);

  useEffect(() => {
    try {
      if (isMobile) {
        const springStrength = 0.08
        const damping = 0.7
        
        const updateMobileMotion = () => {
          try {
            let needsUpdate = false;
            
            rowRefs.current.forEach((row, index) => {
              if (!row || !row.style || !rowPositionsRef.current) return;
              
              const direction = index % 2 === 0 ? 1 : -1;
              const targetPosition = 0;
              
              const distance = targetPosition - (rowPositionsRef.current[index] || 0);
              const force = distance * springStrength;
              
              if (Math.abs(force) > 0.01) {
                needsUpdate = true;
                rowPositionsRef.current[index] = (rowPositionsRef.current[index] || 0) + force * damping * direction;
                row.style.transform = `translateX(${rowPositionsRef.current[index]}px)`;
              }
            });
            
            if (needsUpdate) {
              animationFrameRef.current = requestAnimationFrame(updateMobileMotion);
            } else {
              animationFrameRef.current = null;
            }
          } catch (error) {
            console.warn('MiniProfileGrid: Mobile animation error:', error);
            animationFrameRef.current = null;
          }
        };
      
      const handleTouchStart = (e: TouchEvent) => {
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
        touchStartXRef.current = e.touches[0].clientX;
      };
      
      const handleTouchMove = (e: TouchEvent) => {
        try {
          if (!e.touches[0] || !rowPositionsRef.current) return;
          
          const touchX = e.touches[0].clientX;
          const moveX = touchX - touchStartXRef.current;
          
          rowRefs.current.forEach((row, index) => {
            if (!row || !row.style || !rowPositionsRef.current) return;
            
            const direction = index % 2 === 0 ? 1 : -1;
            const intensity = 1 - (index * 0.1);
            const newPosition = moveX * direction * intensity * 0.5;
            
            rowPositionsRef.current[index] = newPosition;
            row.style.transform = `translateX(${newPosition}px)`;
          });
        } catch (error) {
          console.warn('MiniProfileGrid: Touch move error:', error);
        }
      };
      
      const handleTouchEnd = () => {
        if (!animationFrameRef.current) {
          animationFrameRef.current = requestAnimationFrame(updateMobileMotion);
        }
      };
      
      document.addEventListener('touchstart', handleTouchStart);
      document.addEventListener('touchmove', handleTouchMove);
      document.addEventListener('touchend', handleTouchEnd);
      
      return () => {
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
        document.removeEventListener('touchstart', handleTouchStart);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
      };
    } else {
      // Desktop animation using CSS transforms instead of GSAP
      const handleMouseMove = (e: MouseEvent) => {
        mouseXRef.current = e.clientX;
        
        const maxMoveAmount = 120;
        const inertiaFactors = [0.6, 0.4, 0.3, 0.2, 0.1, 0.05, 0.03];

        rowRefs.current.forEach((row, index) => {
          if (row) {
            const direction = index % 2 === 0 ? 1 : -1;
            const moveAmount = ((mouseXRef.current / window.innerWidth) * maxMoveAmount - maxMoveAmount / 2) * direction;
            
            row.style.transition = `transform ${0.8 + inertiaFactors[index % inertiaFactors.length]}s cubic-bezier(0.25, 0.46, 0.45, 0.94)`;
            row.style.transform = `translateX(${moveAmount}px)`;
          }
        });
      };

      window.addEventListener('mousemove', handleMouseMove);

      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
      };
    }
    } catch (error) {
      console.error('MiniProfileGrid: Animation setup error:', error);
      return () => {}; // Return empty cleanup function on error
    }
  }, [isMobile])

  return (
    <div className={cn("h-full w-full overflow-hidden", className)} ref={gridRef}>
      <section
        className="relative flex h-screen w-full items-center justify-center overflow-hidden"
        style={{
          backgroundColor: gradientColor,
        }}
      >
        <div 
          className={cn(
            "relative z-2 flex-none grid h-[100vh]",
            isMobile 
              ? "w-[160vw] gap-[0.5rem] grid-rows-[repeat(8,1fr)] -translate-y-[20%]"
              : "w-[clamp(132vw,144vw,144vw)] gap-[clamp(0.6rem,2.4vw,0.9rem)] grid-rows-[repeat(7,1fr)]",
            "grid-cols-[100%] transform rotate-[-15deg] origin-center -translate-x-[5%]"
          )}
          style={{
            transformStyle: 'preserve-3d',
            perspective: '1000px'
          }}
        >
          {[...Array(rows)].map((_, rowIndex) => (
            <GridRow
              key={rowIndex}
              rowIndex={rowIndex}
              columns={columns}
              isMobile={isMobile}
              finalItems={finalItems}
              rowRef={(el) => (rowRefs.current[rowIndex] = el)}
            />
          ))}
        </div>
      </section>
    </div>
  );
}
